import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ShieldCheck, Zap, ArrowLeft, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Footer } from "@/components/Footer";

const perks = [
  "Full access to premium listings",
  "Priority zero-throttle downloads",
  "One-tap updates across your library",
  "Early access to new releases",
];

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // {code, discount, final_amount, plan_id}
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    api.get("/subscription/plans").then(({ data }) => setPlans(data)).finally(() => setLoading(false));
  }, []);

  const applyCoupon = async (planId) => {
    if (!coupon) return;
    try {
      const { data } = await api.post("/subscription/validate-coupon", { code: coupon, plan_id: planId });
      setApplied({ ...data, plan_id: planId });
      toast.success(`Coupon ${data.code} applied — you save ₹${data.discount}`);
    } catch (e) {
      setApplied(null);
      toast.error(apiError(e));
    }
  };

  const subscribe = async (plan) => {
    setPayingId(plan.id);
    try {
      const { data } = await api.post("/payments/orders", {
        plan_id: plan.id,
        coupon_code: applied?.plan_id === plan.id ? applied.code : coupon || null,
        customer_id: `web_${Date.now()}`,
        customer_name: "Web Customer",
        customer_email: "customer@crazyone.app",
        customer_phone: "9999999999",
        return_url: `${window.location.origin}/payment-result`,
      });
      if (window.Cashfree && data.payment_session_id) {
        const cashfree = window.Cashfree({ mode: data.mode === "production" ? "production" : "sandbox" });
        await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
      } else {
        toast.error("Payment gateway is not available right now. Please try again later.");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPayingId(null);
    }
  };

  const priceFor = (plan) => (applied?.plan_id === plan.id ? applied.final_amount : plan.amount);

  return (
    <div className="min-h-screen bg-[#08090E]">
      <header className="sticky top-0 z-40 border-b border-[#1E2433]/80 bg-[#08090E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-8">
          <Link to="/" data-testid="pricing-back" className="flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-emerald-400">
            <ArrowLeft className="h-4 w-4" /> Store
          </Link>
        </div>
      </header>

      <main data-testid="page-pricing" className="mx-auto max-w-6xl px-4 py-12 sm:px-8">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
            <Zap className="h-3 w-3" /> Subscription plans
          </span>
          <h1 className="font-display mt-4 text-4xl font-extrabold tracking-tight text-slate-100 sm:text-5xl">
            Pricing &amp; <span className="text-emerald-400">plans</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400">
            Simple, transparent pricing. All amounts are in Indian Rupees (INR ₹) and billed securely via Cashfree.
          </p>
        </div>

        {/* Coupon */}
        <div className="mx-auto mt-8 flex max-w-sm items-center gap-2">
          <div className="relative flex-1">
            <Ticket className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="pricing-coupon"
              value={coupon}
              onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setApplied(null); }}
              placeholder="Coupon code (optional)"
              className="w-full rounded-lg border border-[#1E2433] bg-[#10131B] py-2.5 pl-9 pr-3 text-sm uppercase text-slate-200 outline-none placeholder:normal-case focus:border-emerald-500/60"
            />
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>
        ) : plans.length === 0 ? (
          <p className="mt-10 text-center text-sm text-slate-500">No plans available right now.</p>
        ) : (
          <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2">
            {plans.map((plan, i) => {
              const highlight = i === plans.length - 1;
              const discounted = applied?.plan_id === plan.id;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`pricing-plan-${plan.id}`}
                  className={`relative overflow-hidden rounded-2xl border p-6 ${highlight ? "border-emerald-500/50 bg-[#10131B] co-glow-cyan" : "border-[#1E2433] bg-[#10131B]"}`}
                >
                  {highlight && <span className="absolute right-4 top-4 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-300">Best value</span>}
                  <h3 className="font-display text-2xl font-bold text-slate-100">{plan.name}</h3>
                  <p className="mt-1 text-sm text-slate-400">{plan.description}</p>
                  <div className="mt-5 flex items-end gap-2">
                    {discounted && <span className="font-display text-xl text-slate-600 line-through">₹{plan.amount}</span>}
                    <span className="font-display text-4xl font-extrabold text-slate-100">₹{priceFor(plan)}</span>
                    <span className="mb-1 font-mono text-xs text-slate-500">/ {plan.duration_days} days</span>
                  </div>
                  {discounted && <p className="mt-1 text-xs text-emerald-400">Coupon {applied.code} applied — save ₹{applied.discount}</p>}

                  <ul className="mt-5 space-y-2">
                    {perks.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="h-4 w-4 shrink-0 text-emerald-400" /> {p}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 space-y-2">
                    {coupon && !discounted && (
                      <button data-testid={`pricing-apply-${plan.id}`} onClick={() => applyCoupon(plan.id)} className="w-full rounded-lg border border-emerald-500/40 py-2 text-xs text-emerald-300 hover:bg-emerald-500/10">
                        Apply coupon to this plan
                      </button>
                    )}
                    <button
                      data-testid={`pricing-subscribe-${plan.id}`}
                      onClick={() => subscribe(plan)}
                      disabled={payingId === plan.id}
                      className="co-btn-primary flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm disabled:opacity-60"
                    >
                      {payingId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                      Subscribe — ₹{priceFor(plan)}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <p className="mx-auto mt-8 max-w-xl text-center text-xs text-slate-600">
          Payments are processed securely by Cashfree Payments. By subscribing you agree to our{" "}
          <Link to="/terms" className="text-emerald-400 hover:underline">Terms &amp; Conditions</Link> and{" "}
          <Link to="/refunds" className="text-emerald-400 hover:underline">Refunds &amp; Cancellations</Link> policy.
        </p>
      </main>

      <Footer />
    </div>
  );
}
